<div class="mb-6 flex flex-wrap items-center justify-between gap-4">
  <div>
    <h1 class="!mt-0 !mb-2">Quotes</h1>
    <p class="!mt-0 text-muted-color">Create and manage quotes.</p>
  </div>
  <p-button
    label="New Quote"
    icon="pi pi-plus"
    [loading]="creatingQuote()"
    [disabled]="creatingQuote()"
    (onClick)="newQuote()"
  />
</div>

<div class="flex flex-wrap justify-between items-center gap-4 mb-4">
  <div class="flex items-center gap-4 w-full max-w-2xl">
    <div class="relative w-full max-w-lg">
      <span
        class="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-400 dark:text-gray-500"
      >
        <i class="pi pi-search text-base"></i>
      </span>
      <input
        type="text"
        placeholder="Search by number, customer, or reference..."
        [(ngModel)]="searchTerm"
        (input)="searchSubject.next(searchTerm)"
        class="w-full pl-10 pr-4 py-2.5 rounded-md border bg-surface-switch-0 border-surface-switch-200 placeholder:text-muted-color focus:outline-none focus:border-primary"
      />
    </div>
  </div>
  <div class="flex items-center gap-3">
    <div class="flex items-center gap-2">
      <p-checkbox
        [(ngModel)]="showArchivedQuotes"
        [binary]="true"
        inputId="showArchivedQuotes"
        (onChange)="showArchived()"
      />
      <label
        for="showArchivedQuotes"
        class="text-sm font-medium whitespace-nowrap"
        >Show Archived Quotes</label
      >
    </div>

    <p-button
      label="Clear Filters"
      icon="pi pi-filter-slash"
      severity="secondary"
      (onClick)="clearAllFilters()"
    />
  </div>
</div>

<!-- Header Row - visible only on wide containers -->
<div
  class="@3xl:rounded-xl @3xl:border @3xl:border-surface-switch-200 @3xl:overflow-visible @3xl:bg-surface-switch-50"
>
  <div
    class="hidden @3xl:flex bg-surface-switch-100 items-baseline gap-2 @6xl:gap-4 pt-2 pb-1 px-2 @6xl:pt-3 @6xl:pb-2 @6xl:px-4 text-sm @3xl:text-xs @6xl:text-sm font-bold text-surface-switch-600 uppercase border-b border-surface-switch-200"
  >
    <div class="flex-1 min-w-0 flex items-baseline">
      <app-filter-sort-column-header
        columnName="Number"
        columnKey="code_number"
        [options]="numberOptions"
        [selectedValues]="selectedFilters['code_number']"
        [sortColumn]="sortColumn"
        [sortDirection]="sortDirection"
        (filterChanged)="onFilterChanged('code_number', $event)"
        (sortChanged)="onSortChange($event)"
      ></app-filter-sort-column-header>
    </div>
    <div class="flex-1 min-w-0 flex items-baseline">
      <app-filter-sort-column-header
        columnName="Reference"
        columnKey="reference"
        [options]="referenceOptions"
        [selectedValues]="selectedFilters['reference']"
        [sortColumn]="sortColumn"
        [sortDirection]="sortDirection"
        (filterChanged)="onFilterChanged('reference', $event)"
        (sortChanged)="onSortChange($event)"
      ></app-filter-sort-column-header>
    </div>
    <div class="flex-1 min-w-0 flex items-baseline">
      <app-filter-sort-column-header
        columnName="Customer"
        columnKey="company"
        [options]="companyOptions"
        [selectedValues]="selectedFilters['company']"
        [sortColumn]="sortColumn"
        [sortDirection]="sortDirection"
        (filterChanged)="onFilterChanged('company', $event)"
        (sortChanged)="onSortChange($event)"
      ></app-filter-sort-column-header>
    </div>
    <div class="flex-1 min-w-0 flex items-baseline">
      <app-filter-sort-column-header
        columnName="Total"
        columnKey="total"
        [sortColumn]="sortColumn"
        [sortDirection]="sortDirection"
        (sortChanged)="onSortChange($event)"
      ></app-filter-sort-column-header>
    </div>
    <div class="flex-1 min-w-0 truncate">Status</div>
    <div class="flex-1 min-w-0 flex items-baseline">
      <app-created-date-range-header
        [dateRangeStart]="createdDateRange.start"
        [dateRangeEnd]="createdDateRange.end"
        [sortColumn]="sortColumn"
        [sortDirection]="sortDirection"
        (dateRangeChange)="onCreatedDateRangeChange($event)"
        (sortChanged)="onSortChange($event)"
      ></app-created-date-range-header>
    </div>
    <div class="flex-1 min-w-0 flex items-baseline">
      <app-filter-sort-column-header
        columnName="Created By"
        columnKey="creator_email"
        [options]="creatorOptions"
        [selectedValues]="selectedFilters['creator_email']"
        [sortColumn]="sortColumn"
        [sortDirection]="sortDirection"
        (filterChanged)="onFilterChanged('creator_email', $event)"
        (sortChanged)="onSortChange($event)"
      ></app-filter-sort-column-header>
    </div>
    <div class="w-10 text-center">&nbsp;</div>
  </div>

  @if (quotes$ | async; as quotes) {
    @if (quotes.length > 0) {
      <!-- Quote Rows -->
      <div class="flex flex-col gap-3 @3xl:gap-0">
        @for (quote of quotes; track quote.id) {
          <div
            class="relative rounded-xl border bg-surface-switch-50 overflow-hidden @3xl:rounded-none @3xl:overflow-visible @3xl:p-2 @3xl:pr-0 @6xl:p-4 @6xl:pr-0 @3xl:flex @3xl:items-center @3xl:gap-2 @6xl:gap-4 @3xl:text-xs @6xl:text-sm @3xl:transition-colors @3xl:duration-400 @3xl:ease-out @3xl:hover:bg-surface-switch-100"
            [ngClass]="
              quote.archived_at
                ? 'border-danger'
                : 'border-surface-switch-200 @3xl:border-0 @3xl:border-b @3xl:last:border-b-0'
            "
          >
            <a
              [routerLink]="['/quote', quote.id, quote.current_revision.number]"
              class="hidden @3xl:block absolute inset-0 z-0 cursor-pointer"
              [attr.aria-label]="'View quote ' + quote.code_number"
            ></a>
            <!-- Mobile: Header Row with Number and Actions -->
            <div
              class="flex items-center justify-between m-4 mt-2 mb-3 @3xl:hidden"
            >
              <a
                [routerLink]="[
                  '/quote',
                  quote.id,
                  quote.current_revision.number,
                ]"
                class="text-primary hover:underline whitespace-nowrap font-semibold text-xl"
              >
                {{ quote.code_number }}
              </a>
              <p-button
                text="true"
                severity="secondary"
                icon="pi pi-ellipsis-h"
                (onClick)="quoteMenu.toggle($event); $event.stopPropagation()"
              />
            </div>

            <!-- Mobile: Column Layout for Initial Fields -->
            <div class="flex flex-col gap-4 @3xl:hidden mx-4 mb-2">
              <!-- Reference Row -->
              <div class="flex items-center justify-between">
                <span class="font-semibold text-muted-color"> Reference </span>
                <span>{{ quote.reference || "—" }}</span>
              </div>

              <!-- Customer Row -->
              <div class="flex items-center justify-between gap-4">
                <span class="font-semibold text-muted-color">Customer</span>
                <span class="truncate">{{ quote.company_name || "—" }}</span>
              </div>

              <!-- Total Row -->
              <div class="flex items-center justify-between">
                <span class="font-semibold text-muted-color">Total</span>
                <span>
                  {{
                    quote.current_revision.total
                      | currency: quote.current_revision.currency_code
                  }}
                </span>
              </div>

              <!-- Expanded Details -->
              @if (isExpanded(quote.id)) {
                <div
                  class="expanded-details-collapse w-full"
                  animate.enter="enter-expanded-details-animation"
                  animate.leave="leave-expanded-details-animation"
                >
                  <div
                    class="expanded-details-collapse-inner flex flex-col gap-4"
                  >
                    <!-- Status Row -->
                    <div class="flex items-center justify-between">
                      <span class="font-semibold text-muted-color">Status</span>
                      @if (isApprovalFlowEnabled) {
                        <span>{{
                          getApprovalStatusLabel(
                            quote.current_revision.approval_status
                          )
                        }}</span>
                      } @else {
                        <span>{{ quote.status }}</span>
                      }
                    </div>

                    <!-- Created Row -->
                    <div class="flex items-center justify-between">
                      <span class="font-semibold text-muted-color">
                        Created
                      </span>
                      <span class="whitespace-nowrap">
                        {{ quote.created_date | date: "d MMM yyyy" }}
                      </span>
                    </div>

                    <!-- Creator Row -->
                    <div class="flex items-center justify-between">
                      <span class="font-semibold text-muted-color">
                        Created By
                      </span>
                      <span class="truncate">
                        {{ quote.creator_email || "—" }}
                      </span>
                    </div>
                  </div>
                </div>
              }
            </div>

            <!-- Mobile: Expand/Hide Button -->
            <div class="expand-button w-full @3xl:hidden bg-primary/20">
              <p-button
                [label]="isExpanded(quote.id) ? 'Hide' : 'Expand'"
                [icon]="
                  isExpanded(quote.id)
                    ? 'pi pi-chevron-up'
                    : 'pi pi-chevron-down'
                "
                [style]="{ width: '100%', justifyContent: 'center' }"
                [text]="true"
                severity="secondary"
                (onClick)="toggleExpand(quote.id, $event)"
              />
            </div>

            <!-- Desktop: Number -->
            <div
              class="hidden font-semibold @3xl:block @3xl:flex-1 min-w-0 pointer-events-none"
            >
              {{ quote.code_number }}
            </div>

            <!-- Desktop: Reference -->
            <div
              class="hidden @3xl:block @3xl:flex-1 min-w-0 pointer-events-none"
            >
              <div
                class="truncate text-left"
                [class.text-center]="!quote.reference"
              >
                {{ quote.reference || "—" }}
              </div>
            </div>

            <!-- Desktop: Customer -->
            <div
              class="hidden @3xl:block @3xl:flex-1 min-w-0 pointer-events-none"
            >
              <div
                class="truncate text-left"
                [class.text-center]="!quote.company_name"
              >
                {{ quote.company_name || "—" }}
              </div>
            </div>

            <!-- Desktop: Total -->
            <!-- Here I don't think I need block and flex...  -->
            <div
              class="hidden @3xl:block @3xl:flex-1 min-w-0 pointer-events-none"
            >
              <div class="truncate">
                {{
                  quote.current_revision.total
                    | currency: quote.current_revision.currency_code
                }}
              </div>
            </div>

            <!-- Desktop: Status -->
            <div
              class="hidden @3xl:block @3xl:flex-1 min-w-0 pointer-events-none"
            >
              <span
                class="inline-flex items-center h-8 px-3 text-sm @3xl:text-xs @6xl:text-sm font-medium rounded border"
                [ngClass]="{
                  'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-900/50':
                    quote.current_revision.approval_status === 'NOT_REQUESTED',

                  'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700 dark:hover:bg-blue-900/50':
                    quote.current_revision.approval_status === 'REQUESTED',

                  'bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700 dark:hover:bg-green-900/50':
                    quote.current_revision.approval_status === 'APPROVED',

                  'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700':
                    quote.current_revision.approval_status ===
                    'CHANGES_REQUESTED',
                }"
              >
                @if (isApprovalFlowEnabled) {
                  {{
                    getApprovalStatusLabel(
                      quote.current_revision.approval_status || "NOT_REQUESTED"
                    )
                  }}
                } @else {
                  <span class="truncate block">{{ quote.status }}</span>
                }
              </span>
            </div>

            <!-- Desktop: Created -->
            <div
              class="hidden @3xl:block @3xl:flex-1 min-w-0 pointer-events-none"
            >
              <span class="truncate block">{{
                quote.created_date | date: "d MMM yyyy"
              }}</span>
            </div>

            <!-- Desktop: Creator -->
            <div
              class="hidden @3xl:block @3xl:flex-1 min-w-0 pointer-events-none"
            >
              <div
                class="truncate text-left"
                [class.text-center]="!quote.creator_email"
              >
                {{ quote.creator_email || "—" }}
              </div>
            </div>

            <!-- Desktop: Action Menu -->
            <div class="hidden @3xl:flex justify-center relative z-10">
              <p-button
                text="true"
                icon="pi pi-ellipsis-v"
                styleClass="!text-surface-switch-500"
                (onClick)="quoteMenu.toggle($event); $event.stopPropagation()"
              />
            </div>

            <!-- Popover Menu (shared) -->
            <p-popover #quoteMenu>
              <ng-template #content>
                <div class="flex flex-col min-w-40">
                  <p-button
                    label="Download PDF"
                    icon="pi pi-download"
                    styleClass="w-full"
                    [text]="true"
                    (onClick)="
                      quotesService.downloadPdf(quote); quoteMenu.hide()
                    "
                  />
                  @if (quote.archived_at === null) {
                    <p-button
                      label="Archive Quote"
                      icon="pi pi-inbox"
                      styleClass="w-full"
                      [text]="true"
                      (onClick)="archiveQuote(quote); quoteMenu.hide()"
                    />
                  } @else {
                    <p-button
                      label="Unarchive Quote"
                      icon="pi pi-inbox"
                      styleClass="w-full"
                      [text]="true"
                      (onClick)="unarchiveQuote(quote); quoteMenu.hide()"
                    />
                  }
                  <p-button
                    label="Delete Quote"
                    icon="pi pi-trash"
                    styleClass="w-full"
                    severity="danger"
                    [text]="true"
                    (onClick)="confirmDeleteQuote(quote); quoteMenu.hide()"
                  />
                </div>
              </ng-template>
            </p-popover>
          </div>
        }
      </div>
    } @else {
      <div
        class="text-center text-gray-500 py-6 rounded-xl border border-surface-switch-200 bg-surface-switch-50 @3xl:rounded-none @3xl:border-0"
      >
        No matching quotes found.
      </div>
    }
  } @else {
    <p>No quotes found</p>
  }
</div>

<p-paginator
  [rows]="rows"
  [totalRecords]="totalRecords"
  [first]="first"
  [rowsPerPageOptions]="[10, 20, 50]"
  [showCurrentPageReport]="true"
  currentPageReportTemplate="{first} - {last} of {totalRecords}"
  (onPageChange)="onPageChange($event)"
/>

<shared-confirm-dialog />
